import { useState, useEffect } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { createVirtualModel } from '../../services/virtualModels';

export default function CreateVirtualModelPage() {
  const [activeMenu, setActiveMenu] = useState('虚拟模特库');
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [modelName, setModelName] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [firstFrameDataUrl, setFirstFrameDataUrl] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('00:00:00');
  const [endTime, setEndTime] = useState('00:00:10');
  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    getCurrentUser().then(setUser);
  }, []);

  /** 从视频 URL 提取首帧为图片 data URL（限制尺寸与大小，避免请求体过大） */
  const extractFirstFrame = (videoUrl: string): Promise<string | null> => {
    const MAX_SIZE = 400;
    const MAX_DATA_URL_LEN = 180000;
    const JPEG_QUALITY = 0.7;
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.onloadeddata = () => {
        video.currentTime = 0.5;
      };
      video.onseeked = () => {
        try {
          const w = video.videoWidth;
          const h = video.videoHeight;
          let cw = w;
          let ch = h;
          if (w > MAX_SIZE || h > MAX_SIZE) {
            if (w >= h) {
              cw = MAX_SIZE;
              ch = Math.round((h * MAX_SIZE) / w);
            } else {
              ch = MAX_SIZE;
              cw = Math.round((w * MAX_SIZE) / h);
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(video, 0, 0, w, h, 0, 0, cw, ch);
          let dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
          if (dataUrl.length > MAX_DATA_URL_LEN) {
            dataUrl = canvas.toDataURL('image/jpeg', 0.5);
          }
          if (dataUrl.length > MAX_DATA_URL_LEN) {
            dataUrl = canvas.toDataURL('image/jpeg', 0.35);
          }
          video.src = '';
          resolve(dataUrl.length > MAX_DATA_URL_LEN ? null : dataUrl);
        } catch {
          resolve(null);
        }
      };
      video.onerror = () => {
        video.src = '';
        resolve(null);
      };
      video.src = videoUrl;
    });
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
      setFirstFrameDataUrl(null);
      extractFirstFrame(url).then((dataUrl) => setFirstFrameDataUrl(dataUrl));
    }
  };

  const handleRemoveVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoFile(null);
    setVideoPreview(null);
    setFirstFrameDataUrl(null);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!modelName.trim()) {
      setSubmitError('请输入模特名称');
      return;
    }
    if (!videoFile) {
      setSubmitError('请上传视频片段');
      return;
    }
    if (!user?.id) {
      setSubmitError('请先登录');
      return;
    }
    if (!firstFrameDataUrl) {
      setSubmitError('正在提取视频首帧，请稍候再提交；若一直失败请更换视频');
      return;
    }
    // 限制上传体积，避免请求体过大
    if (videoFile.size > 40 * 1024 * 1024) {
      setSubmitError('视频过大，请上传 40MB 以内的片段');
      return;
    }

    setIsUploading(true);
    try {
      // 将视频转为 base64，后端落盘后供「我的模特」预览播放
      const video_base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('读取视频失败'));
        reader.readAsDataURL(videoFile);
      });

      const result = await createVirtualModel(user.id, {
        name: modelName.trim(),
        image_url: firstFrameDataUrl,
        video_base64,
      });
      if (!result.success) {
        setSubmitError(result.error || '创建失败');
        return;
      }
      setShowSuccessModal(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '创建虚拟模特失败');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/virtual-models');
    }
  };

  const handleBack = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/virtual-models');
    }
  };

  const formatTimeInput = (value: string) => {
    // 只允许输入数字和冒号
    return value.replace(/[^\d:]/g, '');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex min-w-0">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        
        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden min-w-0">
          <div className="max-w-4xl mx-auto w-full min-w-0">
            <div className="flex items-center gap-4 mb-8">
              <button
                onClick={handleBack}
                className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a] border border-gray-800 rounded-lg hover:border-blue-500 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold mb-2">创建虚拟模特</h1>
                <p className="text-gray-400 text-sm">
                  上传视频片段，系统将自动提取人物特征生成虚拟模特
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* 模特名称 */}
              <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-user-line text-blue-400 text-lg"></i>
                  <h3 className="text-lg font-semibold">模特名称</h3>
                  <span className="text-red-500">*</span>
                </div>
                
                <input
                  type="text"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  placeholder="请输入模特名称，例如：张雅琪"
                  className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  maxLength={20}
                />
                <div className="text-xs text-gray-500 mt-2 text-right">
                  {modelName.length} / 20
                </div>
              </div>

              {/* 上传视频 */}
              <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-video-upload-line text-purple-400 text-lg"></i>
                  <h3 className="text-lg font-semibold">上传视频</h3>
                  <span className="text-red-500">*</span>
                </div>
                
                <p className="text-xs text-gray-400 mb-4">
                  支持 MP4、MOV、AVI 格式，建议上传清晰的正面人物视频，时长10-60秒
                </p>

                {videoPreview ? (
                  <div className="relative">
                    <video
                      src={videoPreview}
                      controls
                      className="w-full max-h-96 rounded-xl bg-black"
                    />
                    <button
                      onClick={handleRemoveVideo}
                      className="absolute top-4 right-4 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-white text-xl"></i>
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-700 rounded-xl p-16 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                    <input
                      type="file"
                      accept="video/mp4,video/mov,video/avi"
                      onChange={handleVideoUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-video-add-line text-blue-400 text-4xl"></i>
                    </div>
                    <p className="text-gray-400 mb-2">点击上传视频文件</p>
                    <p className="text-xs text-gray-500">或拖拽文件到此区域</p>
                  </div>
                )}
              </div>

              {/* 视频片段设置 */}
              <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-scissors-cut-line text-green-400 text-lg"></i>
                  <h3 className="text-lg font-semibold">视频片段设置</h3>
                </div>
                
                <p className="text-xs text-gray-400 mb-6">
                  选择视频中需要提取人物特征的片段，建议选择人物清晰、动作自然的部分
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-3">
                      <i className="ri-play-line text-blue-400"></i>
                      <span>片段开始时间</span>
                    </label>
                    <input
                      type="text"
                      value={startTime}
                      onChange={(e) => setStartTime(formatTimeInput(e.target.value))}
                      placeholder="00:00:00"
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-2">格式：时:分:秒 (例如 00:00:05)</p>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-3">
                      <i className="ri-stop-line text-red-400"></i>
                      <span>片段结束时间</span>
                    </label>
                    <input
                      type="text"
                      value={endTime}
                      onChange={(e) => setEndTime(formatTimeInput(e.target.value))}
                      placeholder="00:00:10"
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-2">格式：时:分:秒 (例如 00:00:15)</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-400 text-lg mt-0.5"></i>
                    <div className="text-xs text-gray-300 leading-relaxed">
                      <p className="font-medium text-blue-400 mb-1">提示：</p>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>建议片段时长为 5-30 秒</li>
                        <li>确保人物在画面中清晰可见</li>
                        <li>避免选择光线过暗或过亮的片段</li>
                        <li>人物面部应尽量正对镜头</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* 提交错误提示 */}
              {submitError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {submitError}
                </div>
              )}

              {/* 提交按钮 */}
              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="flex-1 px-6 py-4 bg-[#1a1a1a] border border-gray-700 rounded-xl text-sm font-medium hover:border-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  取消
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!modelName || !videoFile || isUploading}
                  className={`flex-1 px-6 py-4 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    modelName && videoFile && !isUploading
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40'
                      : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>处理中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      <span>创建模特</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              
              <h2 className="text-2xl font-bold mb-3">创建成功</h2>
              
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                虚拟模特已成功创建，系统正在处理视频特征提取，约3-5分钟后可在模特库中查看和使用。
              </p>

              <button
                onClick={handleSuccessConfirm}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 cursor-pointer whitespace-nowrap"
              >
                返回模特库
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
